import {actions} from './state/action';
import {
  ILoginMethod,
  ClientType,
  IAuthModalTitleType,
  IApiParamsAuthResult,
} from './type';
import {pAlert} from '../common/component/PAlert';
import {
  REGION,
  IS_CLIENT,
  ACCESS_TOKEN_KEY,
  ACCESS_TOKEN_REFRESH_TIME_KEY,
  LOCALE,
  AUTH_HOST,
  IS_CBT_OR_PROD,
  WEB_HOST,
} from '../common/constant';
import {Page} from '../common/constant/page';
import {
  getDeviceId,
  getDeviceName,
  getLoginReturnPageInfo,
  submitFormAction,
} from '../common/function';
import {getClientStore, IReduxState} from '../common/store';
import {IUserAgent} from '../common/type';
import {ButtonSet} from '../common/type/alert';
import {deleteCookie, getCookie, setCookie} from '../common/util/cookie';
import {getIsRecent} from '../common/util/date';
import {
  getUrlWithFilteredQuery,
  QUERY_PARAMS_OF_ALL_PAGES,
  routeTo,
} from '../common/util/PageRouter';
import {
  getLocalStorageNumber,
  getLocalStorageObject,
  getLocalStorageString,
  setLocalStorageNumber,
  setLocalStorageString,
} from '../common/util/storage';
import {
  ISendEventParam,
  ISendViewImpParam,
  sendEvent,
  sendViewImp,
} from '../common/util/tracking';
import {getIsWebView} from '../common/util/userAgent';
import {clearUserInfo, removeGaCampaignParams} from '../commonSub/function';

export const LOGIN_METHOD_TEXT: {
  [key in ILoginMethod]: string;
} = {
  FB: 'Facebook',
  GG: 'Google',
  AP: 'Apple',
  LN: 'Line',
  KA: 'Kakao',
  DA: 'Daum',
};

export function submitSocialOAuth({
  loginMethod,
  userAgent,
  redirectUrl = removeGaCampaignParams(),
}: {
  loginMethod: ILoginMethod;
  userAgent: IUserAgent;
  redirectUrl?: string;
}) {
  const _redirectUrl = getUrlWithFilteredQuery(redirectUrl, k =>
    QUERY_PARAMS_OF_ALL_PAGES.includes(k)
  );
  const deviceId = getDeviceId();
  const deviceName = getDeviceName(userAgent);
  const {rawData, osName} = userAgent;
  const params = [
    {name: 'region', value: REGION},
    {
      name: 'client_type',
      value: userAgent.isMobile ? ClientType.Mobile : ClientType.Pc,
    },
    {name: 'session_type', value: 'AW'},
    {
      name: 'login_method',
      value: loginMethod,
    },
    {name: 'user_agent', value: rawData},
    {name: 'os', value: osName},
    {name: 'on_success_url', value: _redirectUrl},
    {name: 'on_fail_url', value: _redirectUrl},
    {name: 'locale', value: LOCALE[REGION]},
    {name: 'device_id', value: deviceId},
    {name: 'device_name', value: deviceName},
  ];
  submitFormAction({url: `${AUTH_HOST}${API_AUTH}/auth/web/oauth`, params});
}

let lastRefreshTime: number | undefined;
// old token은 약 1분간 유효하므로 이 api와 다른 api의 타이밍 이슈는 발생하지 않는다
export function checkAndRefreshAccessToken() {
  if (IS_CLIENT && !getIsWebView()) {
    if (lastRefreshTime === undefined) {
      lastRefreshTime =
        getLocalStorageNumber({key: ACCESS_TOKEN_REFRESH_TIME_KEY}) || 0;
    }
    if (!getIsRecent(lastRefreshTime, 6)) {
      const store = getClientStore()!;
      if (store.getState().auth.user) {
        store.dispatch(actions.fetchRefreshToken());
        lastRefreshTime = Date.now();
        setLocalStorageNumber({
          key: ACCESS_TOKEN_REFRESH_TIME_KEY,
          value: lastRefreshTime,
        });
      }
    }
  }
}

export function deleteAccessTokenAndUser() {
  if (IS_CLIENT) {
    setAccessToken('');
    getClientStore()!.dispatch(actions.setValue('user', undefined));
  }
}

export function getAccessToken() {
  return getCookie(ACCESS_TOKEN_KEY);
}

export function setAccessToken(token: string) {
  if (token) {
    setCookie({key: ACCESS_TOKEN_KEY, value: token, expire: 24 * 14});
    setLocalStorageNumber({
      key: ACCESS_TOKEN_REFRESH_TIME_KEY,
      value: Date.now(),
    });
  } else {
    deleteCookie(ACCESS_TOKEN_KEY);
  }
}

export function startIdVerification({
  sessionId,
  accessToken,
  verificationSessionId,
  isParentVerification = false,
  redirectUrl = location.href,
}: {
  sessionId?: string;
  accessToken?: string;
  verificationSessionId?: string;
  isParentVerification?: boolean;
  redirectUrl?: string;
}) {
  if (sessionId || accessToken) {
    const _redirectUrl = getUrlWithFilteredQuery(redirectUrl, k =>
      QUERY_PARAMS_OF_ALL_PAGES.includes(k)
    );
    const params = [{name: 'redirect_url', value: _redirectUrl}];
    if (sessionId) {
      params.push({name: 'signup_session_id', value: sessionId});
    } else {
      params.push({name: 'access_token', value: accessToken!});
    }
    // 14세 미만 인증 유저가 부모 계정 인증할 때 반드시 필요함
    verificationSessionId &&
      params.push({
        name: 'verification_session_id',
        value: verificationSessionId,
      });
    // TODO: 테스트를 위한 임시 팝업. 필요 없어지면 삭제하자.
    if (!IS_CBT_OR_PROD) {
      pAlert({
        title: '인증 방식을 선택해주세요 (테스트 팝업)',
        buttonSet: ButtonSet.TwoConfirm,
        isForced: true,
        labelConfirm: '드림시큐리티',
        labelSecondConfirm: '테스트',
      }).then(({secondClicked}) => {
        secondClicked && params.push({name: 'test', value: 'true'});
        submitFormAction({
          url: `${AUTH_HOST}${API_AUTH}${
            isParentVerification
              ? '/auth/id/verify/parent/start'
              : '/auth/id/verify/start'
          }`,
          method: 'get',
          params,
        });
      });
    } else {
      submitFormAction({
        url: `${AUTH_HOST}${API_AUTH}${
          isParentVerification
            ? '/auth/id/verify/parent/start'
            : '/auth/id/verify/start'
        }`,
        method: 'get',
        params,
      });
    }
  }
}

export function getStoredApiParamsAuthResult(state: IReduxState) {
  return (
    state.auth.apiParamsAuthResult ||
    (state.common.isAppMounted &&
      getLocalStorageObject<IApiParamsAuthResult>({
        key: AUTH_RESULT_PARAMS_LS_KEY,
      })) ||
    {}
  );
}

export function sendAuthEvent({
  section,
  page,
  actionName,
  customProps,
}: Pick<ISendEventParam, 'actionName' | 'customProps'> &
  Partial<Pick<ISendEventParam, 'section' | 'page'>>) {
  sendEvent({
    section: section ?? '{{section}}',
    page: page ?? '{{page}}',
    action: 'click',
    actionName,
    customProps,
  });
}

export function sendAuthViewImp({
  section,
  page,
  customProps,
  viewImpContents,
}: Pick<ISendViewImpParam, 'customProps' | 'viewImpContents'> &
  Partial<Pick<ISendEventParam, 'section' | 'page'>>) {
  sendViewImp({
    section: section ?? '{{section}}',
    page: page ?? '{{page}}',
    customProps,
    viewImpContents,
  });
}

export function saveReturnPage(url?: string) {
  // 이미 saveReturnPage을 호출했다면, 이후 호출은 무시한다
  // ex) 미로그인 상태로 성인 작품홈 진입하는 경우, Auth.tsx에서 호출하는 saveReturnPage는 무시
  const returnPageInfo = getLocalStorageString({
    key: RETURN_PAGE_INFO_LS_KEY,
  });
  if (!returnPageInfo) {
    setLocalStorageString({
      key: RETURN_PAGE_INFO_LS_KEY,
      value: url || location.pathname + location.search,
    });
  }
}

export function handleClickIntegration() {
  const store = getClientStore();
  if (store) {
    saveReturnPage();
    store.dispatch(
      actions.setValue('authModal', {
        show: false,
        pageInfoAfterClose: undefined,
      })
    );
    routeTo({page: Page.AuthIntegration});
  }
}

export function handleConfirmIntegration(userAgent: IUserAgent | undefined) {
  sendAuthEvent({
    section: 'account_integration_guide',
    page: 'account_integration_guide',
    actionName: 'integration_start',
  });
  if (userAgent) {
    submitSocialOAuth({
      loginMethod: 'DA',
      userAgent,
      redirectUrl: WEB_HOST + getLoginReturnPageInfo(),
    });
  }
}

export function showPAlertDifferentRegion() {
  return pAlert({
    title: t => t('login_global_signedup_web'),
    labelConfirm: t => t('login_global_signedup_web_button'),
    labelClose: t => t('common_close'),
    buttonSet: ButtonSet.CancelAndConfirm,
  });
}

export function showPAlertTermsNotAgreed() {
  pAlert({
    title: t => t('login_global_withdraw_consent_popup_title'),
    desc: t => t('login_global_withdraw_consent_popup_content'),
    isTextAlignCenter: false,
    groupId: 'terms-not-agreed',
  }).then(() => clearUserInfo());
}

export const API_AUTH = '/auth/v1';

export const LOGIN_TITLE_KEY_MAP: {[key in IAuthModalTitleType]?: string} = {
  loginButton: 'login_global_popup',
  onBoarding: 'login_global_onboarding_popup',
  adult: 'login_action_adult_popup',
  waitfree: 'login_action_view_gidamu_episode_popup',
  nextEpisode: 'login_action_view_next_ep_popup',
};

export const LOGIN_KR_TITLE_KEY_MAP: {
  [key in IAuthModalTitleType]?: string;
} = {
  loginButton: '',
  adult: 'login_action_adult_popup',
  waitfree: 'login_action_view_gidamu_episode_popup',
  nextEpisode: 'login_action_view_next_episode_popup',
};

export const AUTH_RESULT_PARAMS_LS_KEY = 'auth-result-params';
export const TERMS_CHECKED_INFO_LS_KEY = 'terms-checked-info';
export const RETURN_PAGE_INFO_LS_KEY = 'return-page-info';
